/**
 * useLgpdReportTypes / useGenerateLgpdReport — Brief 5 B4 Relatórios LGPD.
 *
 * Sprint Frontend Brief 5 · Tela 1. Consome o módulo lgpd-reports (Fatia 1 BE):
 *   - GET  /v1/lgpd/reports/types     → descriptors (forensic + availableToCaller)
 *   - POST /v1/lgpd/reports/generate  → GeneratedLgpdReport (document + hash)
 *
 * Gate server-side: `lgpd-requests-management`. Tipos forenses exigem também
 * `audit-events-read-sensitive` (403 no generate). `types` é estável (lista de
 * descritores) → staleTime alto. `generate` é ação pontual (não cacheia).
 */

import { useMutation, useQuery } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type {
  GenerateLgpdReportRequest,
  GeneratedLgpdReport,
  LgpdReportTypeDescriptor,
} from '../features/dpo/relatorios/types';

const TYPES_QUERY_KEY = 'lgpd-report-types';
const TYPES_STALE_TIME = 5 * 60_000;

export function useLgpdReportTypes(enabled = true): {
  data: LgpdReportTypeDescriptor[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: [TYPES_QUERY_KEY],
    queryFn: () => api.listLgpdReportTypes(),
    staleTime: TYPES_STALE_TIME,
    retry: 1,
    enabled,
  });

  return {
    data: query.data?.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useGenerateLgpdReport(): ReturnType<
  typeof useMutation<GeneratedLgpdReport, Error, GenerateLgpdReportRequest>
> {
  return useMutation<GeneratedLgpdReport, Error, GenerateLgpdReportRequest>({
    mutationFn: (body: GenerateLgpdReportRequest) => api.generateLgpdReport(body),
  });
}
